import * as yup from 'yup';
import { TFunction } from 'i18next';
import {
  ConditionOperation,
  ConditionType,
  ReferenceType,
} from 'api/models/FieldTemplate';

function getConditionValidationSchema(t: TFunction) {
  const baseCondition = {
    type: yup
      .string()
      .required(t('general.errors.mandatoryField'))
      .oneOf([ConditionType.condition]),
    operation: yup
      .string()
      .required(t('general.errors.mandatoryField'))
      .oneOf(Object.values(ConditionOperation)),
  };

  const patientFieldReference = yup.object().shape({
    ...baseCondition,
    reference: yup.object().shape({
      type: yup
        .string()
        .required()
        .oneOf([ReferenceType.patientFieldReference]),
      attribute: yup
        .string()
        .transform((value) =>
          typeof value === 'string' ? value.trim() : value
        )
        .required(t('general.errors.mandatoryField'))
        .min(1, t('general.errors.mandatoryField'))
        .test(
          'not-empty',
          t('general.errors.mandatoryField'),
          val => !!val && val.trim().length > 0,
        ),
      valueType: yup.string().nullable(),
    }),
    value: yup.mixed().notRequired(),
  });

  const formReference = yup.object().shape({
    ...baseCondition,
    reference: yup.object().shape({
      type: yup.string()
        .required()
        .oneOf([ReferenceType.formReference]),
      formTemplateId: yup.string()
        .required(t('general.errors.mandatoryField')),
      attribute: yup.string()
        .transform(value => typeof value === 'string' ? value.trim() : value)
        .required(t('general.errors.mandatoryField'))
        .min(1, t('general.errors.mandatoryField'))
        .test(
          'not-empty',
          t('general.errors.mandatoryField'),
          val => !!val && val.trim().length > 0
        ),
      formStatus: yup.array().of(yup.string()).notRequired(),
    }),
    value: yup.mixed()
      .required(t('general.errors.mandatoryField'))
      .test('required-value', t('general.errors.mandatoryField'), val => {
        if (val === undefined || val === null) return false;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'string') return val.trim().length > 0;
        return false;
      }),
  });


  const formFieldReference = yup.object().shape({
    ...baseCondition,
    reference: yup.object().shape({
      type: yup.string().required().oneOf([ReferenceType.formFieldReference]),
      fieldTemplateId: yup.string().required(t('general.errors.mandatoryField')),
      valueType: yup.string().nullable(),
      attribute: yup
        .string()
        .transform((value) =>
          typeof value === 'string' ? value.trim() : value
        )
        .required(t('general.errors.mandatoryField'))
        .min(1, t('general.errors.mandatoryField'))
        .test(
          'not-empty',
          t('general.errors.mandatoryField'),
          val => !!val && val.trim().length > 0),
    }),
    value: yup.mixed().notRequired(),
  });

  return yup.lazy((condition: any) => {
    switch (condition?.reference?.type) {
      case ReferenceType.patientFieldReference:
        return patientFieldReference;
      case ReferenceType.formReference:
        return formReference;
      case ReferenceType.formFieldReference:
        return formFieldReference;
      default:
        return yup.object().shape(baseCondition);
    }
  });
}

export function getExportValidationSchema(t: TFunction) {
  return yup.object().shape({
    filters: yup
      .array()
      .of(getConditionValidationSchema(t))
      .min(1, t('general.errors.minimumOneFilter')),
  });
}
